import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, SkipBack, SkipForward, X, Download, AlertTriangle } from 'lucide-react';
import { useAudio } from '../../context/AudioContext';

const fmtTime = (s) => {
  if (!isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
};

export const MiniPlayer = () => {
  const { state, pause, resume, stop, next, previous, seek, reciters } = useAudio();
  const visible = !!state.currentTrack || state.error;
  if (!visible) return null;

  const reciter = reciters.find((r) => r.id === state.reciterId);
  const t = state.currentTrack;
  const pct = state.duration ? (state.progress / state.duration) * 100 : 0;

  const modeLabel = ({
    ayah: 'Verset', surah: 'Sourate', hizb: 'Ḥizb', quran: 'Coran complet',
    memorization: 'Mémorisation',
  })[state.mode] || '';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        className="fixed bottom-3 left-3 right-3 z-[100] sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-md"
        data-testid="mini-player"
      >
        <div className="bg-surface/95 backdrop-blur-md border border-border rounded-xl shadow-2xl p-3">
          <div className="flex items-center gap-3">
            {/* Now-playing meta */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                {modeLabel && (
                  <span className="text-[10px] uppercase tracking-wider text-primary">{modeLabel}</span>
                )}
                <span className="text-[10px] text-text-muted">· {reciter?.label || state.reciterId}</span>
              </div>
              {t ? (
                <p className="text-sm font-medium text-text-primary truncate">{t.title}</p>
              ) : (
                <p className="text-sm text-text-secondary">Aucune lecture en cours</p>
              )}
              {state.error && (
                <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {state.error}
                </p>
              )}
            </div>

            {/* Controls */}
            <div className="flex items-center gap-1">
              <button
                onClick={previous}
                disabled={!t}
                className="p-2 rounded-md hover:bg-surface-elevated disabled:opacity-30 transition-colors"
                aria-label="Précédent"
                data-testid="mini-player-prev"
              >
                <SkipBack className="w-4 h-4 text-text-primary" />
              </button>
              {state.isPlaying ? (
                <button
                  onClick={pause}
                  className="p-2 rounded-full bg-primary text-black hover:bg-primary/90 transition-colors"
                  aria-label="Pause"
                  data-testid="mini-player-pause"
                >
                  <Pause className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={resume}
                  disabled={!t}
                  className="p-2 rounded-full bg-primary text-black hover:bg-primary/90 disabled:opacity-30 transition-colors"
                  aria-label="Lecture"
                  data-testid="mini-player-play"
                >
                  <Play className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={next}
                disabled={!t}
                className="p-2 rounded-md hover:bg-surface-elevated disabled:opacity-30 transition-colors"
                aria-label="Suivant"
                data-testid="mini-player-next"
              >
                <SkipForward className="w-4 h-4 text-text-primary" />
              </button>
              <button
                onClick={stop}
                className="p-2 rounded-md hover:bg-surface-elevated transition-colors"
                aria-label="Stop"
                data-testid="mini-player-stop"
              >
                <X className="w-4 h-4 text-text-muted" />
              </button>
            </div>
          </div>

          {/* Progress */}
          <div className="mt-2">
            <div
              className="h-1 bg-surface-elevated rounded-full overflow-hidden cursor-pointer"
              onClick={(e) => {
                if (!state.duration) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const ratio = (e.clientX - rect.left) / rect.width;
                seek(ratio * state.duration);
              }}
            >
              <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between mt-1 text-[10px] text-text-muted">
              <span>{fmtTime(state.progress)}</span>
              <span>{fmtTime(state.duration)}</span>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MiniPlayer;
